import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Globe2, Search, RefreshCw, KeyRound, Boxes, Network, FolderKanban, Lock, Unlock, Shield, ShieldOff, FileKey2, Plus, Trash2, Save, Loader2, Mail, BadgeDollarSign, CircleAlert, Link2, Unlink, ExternalLink, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import DashboardShell from '../components/DashboardShell';
import ModuleDemoVideo from '../components/ModuleDemoVideo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const TABS = [
  { id: 'search', label: 'Search & Register' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'operations', label: 'Live Operations' },
  { id: 'settings', label: 'Contacts & Portfolios' },
];

const DEFAULT_CONTACT = {
  first_name: '',
  last_name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
  email: '',
  phone: '',
  company: '',
  nickname: '',
};

const normalizeDomainQuery = (value) => {
  const cleaned = (value || '').trim().toLowerCase();
  if (!cleaned) return '';
  return cleaned.includes('.') ? cleaned : `${cleaned}.com`;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (Number.isNaN(amount)) return '—';
  return `$${amount.toFixed(2)}`;
};

const DEFAULT_DOMAIN_CREDIT_PACKS = [
  {
    id: 'pack_10',
    label: '$10 Domain Credit',
    amount_cents: 1000,
    usd: 10,
    description: 'Starter wallet for a single low-cost domain.',
  },
  {
    id: 'pack_25',
    label: '$25 Domain Credit',
    amount_cents: 2500,
    usd: 25,
    description: 'Register or renew domains from your Snagcart wallet.',
  },
  {
    id: 'pack_50',
    label: '$50 Domain Credit',
    amount_cents: 5000,
    usd: 50,
    description: 'Best for multiple domains or premium TLDs.',
  },
];

const checkoutErrorMessage = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
};

function DomainCreditsPanel({
  balanceUsd,
  balanceCents,
  packs,
  enabled,
  stripeConfigured,
  onRefresh,
  onBalanceChange,
  open: openProp,
  onOpenChange,
}) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = typeof openProp === 'boolean' ? openProp : internalOpen;
  const setOpen = (next) => {
    if (typeof onOpenChange === 'function') onOpenChange(next);
    if (typeof openProp !== 'boolean') setInternalOpen(next);
  };
  const [busyPack, setBusyPack] = useState('');
  const [mockSession, setMockSession] = useState(null);
  const displayPacks = (packs && packs.length > 0) ? packs : DEFAULT_DOMAIN_CREDIT_PACKS;

  const buyPack = async (packId) => {
    if (stripeConfigured === false) {
      toast.error('Configure Stripe in Settings → API Keys (or platform billing) to buy domain credit.');
      return;
    }
    setBusyPack(packId);
    try {
      const origin = window.location.origin;
      const res = await fetch(`${API_URL}/api/domain-credits/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          pack_id: packId,
          region: 'us',
          success_url: `${origin}/domains-hosting?domain_credits_payment=stripe&session_id={SESSION_ID}`,
          cancel_url: `${origin}/domains-hosting?domain_credits_payment=cancelled`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(checkoutErrorMessage(data.detail, 'Checkout failed'));
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      if (data.mode === 'mock') {
        setMockSession(data);
        return;
      }
      toast.error('Could not start checkout');
    } catch (error) {
      toast.error(error.message || 'Could not start checkout');
    } finally {
      setBusyPack('');
    }
  };

  const confirmMock = async () => {
    if (!mockSession?.session_id) return;
    try {
      const res = await fetch(`${API_URL}/api/domain-credits/checkout/${encodeURIComponent(mockSession.session_id)}/confirm-mock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(checkoutErrorMessage(data.detail, 'Mock payment failed'));
      toast.success(data.message || `Added ${formatCurrency((data.amount_cents || 0) / 100)} domain credit`);
      setMockSession(null);
      setOpen(false);
      onBalanceChange?.(data.balance_usd, data.balance_cents);
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || 'Mock payment failed');
    }
  };

  const packButtons = (
    <div className="space-y-2" data-testid="domain-credits-pack-list">
      <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Buy credit packs</p>
      {displayPacks.map((pack) => (
        <button
          key={pack.id}
          type="button"
          onClick={() => buyPack(pack.id)}
          disabled={!!busyPack}
          className="w-full rounded-xl border border-white/10 bg-white/[0.04] p-3 text-left hover:border-amber-400/40 disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`domain-credits-pack-${pack.id}`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium text-white">{pack.label}</span>
            <span className="text-sm text-amber-300">${pack.usd}</span>
          </div>
          <p className="text-xs text-white/50 mt-1">{pack.description}</p>
        </button>
      ))}
    </div>
  );

  const modal = open ? createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4"
      data-testid="domain-credits-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setOpen(false);
          setMockSession(null);
        }
      }}
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Domain credit</h3>
          <button type="button" onClick={() => { setOpen(false); setMockSession(null); }} className="text-zinc-400 hover:text-white">×</button>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Balance: <span className="text-white font-semibold">{formatCurrency(balanceUsd)}</span>
          {' · '}Used when you register or attach domains
        </p>
        {enabled === false ? (
          <p className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
            Domain credits are temporarily disabled on this server. Pack options are shown for reference.
          </p>
        ) : null}
        {stripeConfigured === false ? (
          <div
            className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 space-y-2"
            data-testid="domain-credits-stripe-missing"
          >
            <p>Configure Stripe to complete purchases (Settings → API Keys, or platform billing).</p>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/settings'); }}
              className="rounded-md bg-amber-500/30 px-3 py-1.5 text-xs font-semibold text-amber-50 hover:bg-amber-500/40"
              data-testid="domain-credits-configure-stripe"
            >
              Open Settings
            </button>
          </div>
        ) : null}
        {mockSession ? (
          <div className="space-y-3">
            <p className="text-sm text-amber-100">Mock payment — confirm to add {formatCurrency(mockSession.amount_cents / 100)} credit.</p>
            <Button onClick={confirmMock} className="w-full bg-amber-500 text-black hover:bg-amber-400">Confirm mock payment</Button>
          </div>
        ) : packButtons}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div
        className="flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-xs text-amber-100"
        data-testid="domain-credits-balance"
      >
        <BadgeDollarSign className="h-3.5 w-3.5" />
        <span className="font-semibold text-white">{formatCurrency(balanceUsd)}</span>
        <span className="text-amber-200/80">credit</span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-md bg-amber-500/30 px-2 py-0.5 text-[11px] font-medium text-amber-50 hover:bg-amber-500/40"
          data-testid="domain-credits-buy-button"
        >
          Buy credits
        </button>
      </div>
      {modal}
    </>
  );
}

function DomainCreditsBuySection({
  balanceUsd,
  packs,
  enabled,
  stripeConfigured,
  onOpenModal,
  onBuyPack,
}) {
  const navigate = useNavigate();
  const displayPacks = (packs && packs.length > 0) ? packs : DEFAULT_DOMAIN_CREDIT_PACKS;

  return (
    <section
      className="rounded-[28px] border border-amber-400/20 bg-amber-400/[0.06] p-5 sm:p-6"
      data-testid="domain-credits-buy-section"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.24em] text-amber-200/80">Domain wallet</p>
          <h2 className="text-xl font-semibold text-white">Buy domain credit</h2>
          <p className="max-w-2xl text-sm text-zinc-400">
            Balance {formatCurrency(balanceUsd)}. Purchase credit here, then register domains at live prices.
            {enabled === false ? ' Domain credits are currently disabled on this server.' : ''}
          </p>
          {stripeConfigured === false ? (
            <p className="text-sm text-amber-100" data-testid="domain-credits-stripe-missing-inline">
              Stripe is not configured yet — pack buttons stay visible. Add a Stripe secret key in Settings to check out.
              {' '}
              <button type="button" className="underline underline-offset-2" onClick={() => navigate('/settings')}>
                Open Settings
              </button>
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          onClick={onOpenModal}
          className="shrink-0 bg-amber-500 text-black hover:bg-amber-400"
          data-testid="domain-credits-open-modal-button"
        >
          Open credit packs
        </Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="domain-credits-inline-packs">
        {displayPacks.map((pack) => (
          <button
            key={`inline-${pack.id}`}
            type="button"
            onClick={() => (onBuyPack ? onBuyPack(pack.id) : onOpenModal?.())}
            className="rounded-2xl border border-white/10 bg-black/20 p-4 text-left hover:border-amber-400/40"
            data-testid={`domain-credits-inline-pack-${pack.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-white">{pack.label}</span>
              <span className="text-amber-300">${pack.usd}</span>
            </div>
            <p className="mt-1 text-xs text-zinc-400">{pack.description}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

const StatCard = ({ label, value, hint, accent, testId }) => (
  <div className="rounded-[28px] border border-white/10 bg-white/[0.03] p-5" data-testid={testId}>
    <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">{label}</p>
    <p className={`mt-3 text-3xl font-semibold ${accent}`}>{value}</p>
    <p className="mt-2 text-sm text-zinc-400">{hint}</p>
  </div>
);

const SectionHeading = ({ eyebrow, title, subtitle, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <p className="text-xs uppercase tracking-[0.28em] text-emerald-300/80">{eyebrow}</p>
    <h2 className="text-2xl font-semibold text-white">{title}</h2>
    {subtitle ? <p className="max-w-3xl text-sm text-zinc-400">{subtitle}</p> : null}
  </div>
);

export default function DomainsHostingDashboard() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const [activeTab, setActiveTab] = useState('search');
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [overview, setOverview] = useState(null);
  const [prices, setPrices] = useState({});
  const [orders, setOrders] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [domainDetails, setDomainDetails] = useState(null);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [contactProfiles, setContactProfiles] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [registeredNameservers, setRegisteredNameservers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [registrationError, setRegistrationError] = useState('');
  const [portfolioName, setPortfolioName] = useState('');
  const [portfolioAssignName, setPortfolioAssignName] = useState('');
  const [portfolioDomains, setPortfolioDomains] = useState('');
  const [transferStatus, setTransferStatus] = useState(null);
  const [nameserverText, setNameserverText] = useState('');
  const [contactForm, setContactForm] = useState(DEFAULT_CONTACT);
  const [domainContacts, setDomainContacts] = useState({ registrant: '', administrative: '', technical: '', billing: '' });
  const [dnsForm, setDnsForm] = useState({ type: 'A', host: '@', value: '', ttl: 3600, distance: '' });
  const [registeredNsForm, setRegisteredNsForm] = useState({ new_host: '', ip1: '', ip2: '' });
  const [websites, setWebsites] = useState([]);
  const [registerMode, setRegisterMode] = useState('portfolio');
  const [registerWebsiteId, setRegisterWebsiteId] = useState('');
  const [attachWebsiteId, setAttachWebsiteId] = useState('');
  const [domainConnection, setDomainConnection] = useState(null);
  const [domainCreditBalanceUsd, setDomainCreditBalanceUsd] = useState(0);
  const [domainCreditBalanceCents, setDomainCreditBalanceCents] = useState(0);
  const [domainCreditPacks, setDomainCreditPacks] = useState(DEFAULT_DOMAIN_CREDIT_PACKS);
  const [domainCreditsEnabled, setDomainCreditsEnabled] = useState(true);
  const [domainCreditsStripeConfigured, setDomainCreditsStripeConfigured] = useState(null);
  const [domainCreditsModalOpen, setDomainCreditsModalOpen] = useState(false);

  const selectedDomainData = useMemo(
    () => domains.find((domain) => domain.domain_name === selectedDomain) || null,
    [domains, selectedDomain],
  );

  const authorizedFetch = async (path, options = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const detail = payload?.detail;
      const errorMessage = typeof detail === 'string'
        ? detail
        : (detail?.message || payload?.message || 'Request failed');
      const err = new Error(errorMessage);
      err.status = response.status;
      err.detail = detail;
      throw err;
    }
    return payload;
  };

  const loadDomainCredits = async () => {
    try {
      // Catalog must be authenticated — entitlement middleware gates /api/domain-credits.
      // An unauthenticated fetch returned 401 and left packs=[], hiding buy UI.
      const [balRes, catData] = await Promise.all([
        authorizedFetch('/api/domain-credits/balance'),
        authorizedFetch('/api/domain-credits/catalog'),
      ]);
      setDomainCreditBalanceUsd(balRes.balance_usd ?? 0);
      setDomainCreditBalanceCents(balRes.balance_cents ?? 0);
      const packs = Array.isArray(catData.packs) && catData.packs.length > 0
        ? catData.packs
        : DEFAULT_DOMAIN_CREDIT_PACKS;
      setDomainCreditPacks(packs);
      setDomainCreditsEnabled(catData.enabled !== false && balRes.enabled !== false);
      setDomainCreditsStripeConfigured(
        typeof catData.stripe_configured === 'boolean' ? catData.stripe_configured : null,
      );
    } catch {
      setDomainCreditPacks(DEFAULT_DOMAIN_CREDIT_PACKS);
    }
  };

  const loadBaseData = async () => {
    setLoading(true);
    try {
      const [overviewResult, priceResult, domainResult, orderResult, contactResult, portfolioResult, websitesResult] = await Promise.allSettled([
        authorizedFetch('/api/domains/account/overview'),
        authorizedFetch('/api/domains/prices'),
        authorizedFetch('/api/my/domains'),
        authorizedFetch('/api/domains/orders'),
        authorizedFetch('/api/domains/contact-profiles'),
        authorizedFetch('/api/domains/portfolios'),
        authorizedFetch('/api/websites'),
      ]);

      const overviewData = overviewResult.status === 'fulfilled' ? overviewResult.value : null;
      const priceData = priceResult.status === 'fulfilled' ? priceResult.value : null;
      const domainData = domainResult.status === 'fulfilled' ? domainResult.value : [];
      const orderData = orderResult.status === 'fulfilled' ? orderResult.value : [];
      const contactData = contactResult.status === 'fulfilled' ? contactResult.value : [];
      const portfolioData = portfolioResult.status === 'fulfilled' ? portfolioResult.value : [];
      const websitesData = websitesResult.status === 'fulfilled' ? websitesResult.value : [];

      if (overviewData) {
        setOverview(overviewData);
        if (overviewData.domain_credits_balance_usd != null) {
          setDomainCreditBalanceUsd(overviewData.domain_credits_balance_usd);
          setDomainCreditBalanceCents(overviewData.domain_credits_balance_cents ?? 0);
        }
      }
      if (priceData) setPrices(priceData.prices || {});
      setDomains(domainData || []);
      setOrders(orderData || []);
      setContactProfiles(contactData || []);
      setPortfolios(portfolioData || []);
      setWebsites(Array.isArray(websitesData) ? websitesData : []);
      const nextSelected = selectedDomain || domainData?.[0]?.domain_name || '';
      setSelectedDomain(nextSelected);
      await loadDomainCredits();
    } catch (error) {
      toast.error(error.message || 'Failed to load the domains dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadDomainDetails = async (domainName) => {
    if (!domainName) {
      setDomainDetails(null);
      setDnsRecords([]);
      setRegisteredNameservers([]);
      setDomainConnection(null);
      return;
    }
    try {
      const [detailsResult, dnsResult, nsResult, connectionResult] = await Promise.allSettled([
        authorizedFetch(`/api/domains/${encodeURIComponent(domainName)}/details`),
        authorizedFetch(`/api/domains/${encodeURIComponent(domainName)}/dns-records`),
        authorizedFetch(`/api/domains/${encodeURIComponent(domainName)}/registered-nameservers`),
        authorizedFetch(`/api/domains/${encodeURIComponent(domainName)}/connection`),
      ]);

      const detailsData = detailsResult.status === 'fulfilled' ? detailsResult.value : null;
      const dnsData = dnsResult.status === 'fulfilled' ? dnsResult.value : [];
      const nsData = nsResult.status === 'fulfilled' ? nsResult.value : [];
      const connectionData = connectionResult.status === 'fulfilled' ? connectionResult.value : null;

      if (detailsData) {
        setDomainDetails(detailsData);
        setNameserverText((detailsData.nameservers || []).join('\n'));
        setDomainContacts({
          registrant: detailsData.contact_ids?.registrant || '',
          administrative: detailsData.contact_ids?.administrative || '',
          technical: detailsData.contact_ids?.technical || '',
          billing: detailsData.contact_ids?.billing || '',
        });
      }
      setDnsRecords(dnsData || []);
      setRegisteredNameservers(nsData || []);
      setDomainConnection(connectionData);
      if (connectionData?.website_id) {
        setAttachWebsiteId(connectionData.website_id);
      }

    } catch (error) {
      toast.error(error.message || 'Failed to load domain details');
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadBaseData();
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (params.get('domain_credits_payment') === 'stripe' && sessionId && token) {
      fetch(`${API_URL}/api/domain-credits/checkout/${encodeURIComponent(sessionId)}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.payment_status === 'paid') {
            toast.success(data.message || `Domain credit added. Balance: ${formatCurrency(data.balance_usd ?? (data.balance_cents || 0) / 100)}`);
            loadBaseData();
          }
        })
        .catch(() => {});
      window.history.replaceState({}, '', '/domains-hosting');
    }
  }, [token, API_URL]);

  useEffect(() => {
    if (selectedDomain) {
      const timer = window.setTimeout(() => {
        loadDomainDetails(selectedDomain);
      }, 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [selectedDomain]);

  const performAction = async (actionKey, action, successMessage, afterAction) => {
    setBusyAction(actionKey);
    try {
      const result = await action();
      toast.success(successMessage || result.message || result.detail || 'Done');
      if (afterAction) {
        await afterAction(result);
      }
      return result;
    } catch (error) {
      toast.error(error.message || 'Action failed');
      return null;
    } finally {
      setBusyAction('');
    }
  };

  const searchDomain = async () => {
    const normalized = normalizeDomainQuery(searchQuery);
    if (!normalized) {
      toast.error('Enter a domain to search');
      return;
    }
    setBusyAction('search-domain');
    try {
      const data = await authorizedFetch(`/api/domains/check?domain_name=${encodeURIComponent(normalized)}`);
      setSearchResult(data);
      setSearchQuery(normalized);
    } catch (error) {
      toast.error(error.message || 'Search failed');
    } finally {
      setBusyAction('');
    }
  };

  const registerDomain = async (attachToWebsiteId = null) => {
    if (!searchResult?.available) return;
    const registrationCost = Number(searchResult?.price_registration || 0);
    if (insufficientFunds) {
      const message = `Insufficient domain credit. You need ${formatCurrency(registrationCost)} but have ${formatCurrency(domainCreditBalanceUsd)}. Buy domain credit to continue.`;
      setRegistrationError(message);
      toast.error(message);
      return;
    }
    const targetWebsiteId = attachToWebsiteId || (registerMode === 'website' ? registerWebsiteId : null);
    if (registerMode === 'website' && !targetWebsiteId) {
      toast.error('Select a website to attach this domain');
      return;
    }
    setBusyAction(targetWebsiteId ? 'register-attach' : 'register-standalone');
    setRegistrationError('');
    try {
      await authorizedFetch('/api/domains', {
        method: 'POST',
        body: JSON.stringify({
          domain_name: searchResult.domain_name,
          period_years: 1,
          currency: 'USD',
          privacy: true,
          auto_renew: true,
          expected_price_usd: registrationCost,
          website_id: targetWebsiteId || undefined,
          configure_dns: Boolean(targetWebsiteId),
        }),
      });
      toast.success(targetWebsiteId ? 'Domain registered and attached' : 'Domain registered');
      await loadBaseData();
      setSelectedDomain(searchResult.domain_name);
      setActiveTab('operations');
    } catch (error) {
      const message = error.message || 'Registration failed';
      setRegistrationError(message);
      if (error.status === 402) {
        toast.error(`${message} — buy domain credit to continue.`, { duration: 8000 });
      } else {
        toast.error(message);
      }
    } finally {
      setBusyAction('');
    }
  };

  const attachDomain = async () => {
    if (!selectedDomain || !attachWebsiteId) {
      toast.error('Select a domain and website');
      return;
    }
    await performAction(
      'attach-domain',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/attach`, {
        method: 'POST',
        body: JSON.stringify({ website_id: attachWebsiteId, configure_dns: true }),
      }),
      'Domain attached to website',
      async () => {
        await loadBaseData();
        await loadDomainDetails(selectedDomain);
      },
    );
  };

  const detachDomain = async () => {
    if (!selectedDomain) return;
    await performAction(
      'detach-domain',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/detach`, { method: 'POST' }),
      'Domain detached',
      async () => {
        setAttachWebsiteId('');
        await loadBaseData();
        await loadDomainDetails(selectedDomain);
      },
    );
  };

  const configureHostingDns = async () => {
    if (!selectedDomain) return;
    await performAction(
      'configure-dns',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/configure-dns`, { method: 'POST' }),
      'DNS configured for Snagcart hosting',
      async () => loadDomainDetails(selectedDomain),
    );
  };

  const domainStatusLabel = (status) => {
    const map = {
      none: 'Not attached',
      attached: 'Attached',
      dns_pending: 'DNS pending',
      live: 'Live on domain',
    };
    return map[status] || status || 'Unknown';
  };

  const toggleLock = async (locked) => {
    if (!selectedDomain) return;
    await performAction(
      `lock-${locked}`,
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/lock`, {
        method: 'POST',
        body: JSON.stringify({ locked }),
      }),
      locked ? 'Domain locked' : 'Domain unlocked',
      async () => {
        await loadBaseData();
        await loadDomainDetails(selectedDomain);
      },
    );
  };

  const togglePrivacy = async (enabled) => {
    if (!selectedDomain) return;
    await performAction(
      `privacy-${enabled}`,
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/privacy`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
      enabled ? 'WHOIS privacy enabled' : 'WHOIS privacy disabled',
      async () => {
        await loadBaseData();
        await loadDomainDetails(selectedDomain);
      },
    );
  };

  const toggleAutoRenew = async (enabled) => {
    if (!selectedDomain) return;
    await performAction(
      `auto-renew-${enabled}`,
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/auto-renewal`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      }),
      enabled ? 'Auto-renew enabled' : 'Auto-renew disabled',
      async () => {
        await loadBaseData();
        await loadDomainDetails(selectedDomain);
      },
    );
  };

  const renewDomain = async () => {
    if (!selectedDomain) return;
    await performAction(
      'renew-domain',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/renew`, {
        method: 'POST',
        body: JSON.stringify({ years: 1 }),
      }),
      'Renewal submitted',
      async () => {
        await loadBaseData();
        await loadDomainDetails(selectedDomain);
      },
    );
  };

  const requestTransferStatus = async () => {
    if (!selectedDomain) return;
    await performAction(
      'transfer-status',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/transfer-status`),
      'Transfer status refreshed',
      async (result) => setTransferStatus(result),
    );
  };

  const requestAuthCode = async () => {
    if (!selectedDomain) return;
    await performAction(
      'auth-code',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/retrieve-auth-code`, { method: 'POST' }),
      'Auth code email requested',
    );
  };

  const saveNameservers = async () => {
    if (!selectedDomain) return;
    const nameservers = nameserverText.split('\n').map((item) => item.trim()).filter(Boolean);
    await performAction(
      'save-nameservers',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/nameservers`, {
        method: 'PUT',
        body: JSON.stringify({ nameservers }),
      }),
      'Nameservers updated',
      async () => {
        await loadBaseData();
        await loadDomainDetails(selectedDomain);
      },
    );
  };

  const saveDnsRecord = async () => {
    if (!selectedDomain || !dnsForm.value.trim()) {
      toast.error('Record value is required');
      return;
    }
    await performAction(
      'save-dns',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/dns-records`, {
        method: 'POST',
        body: JSON.stringify({
          type: dnsForm.type,
          host: dnsForm.host,
          value: dnsForm.value,
          ttl: Number(dnsForm.ttl) || 3600,
          distance: dnsForm.distance ? Number(dnsForm.distance) : undefined,
        }),
      }),
      'DNS record added',
      async () => {
        await loadDomainDetails(selectedDomain);
        setDnsForm({ type: 'A', host: '@', value: '', ttl: 3600, distance: '' });
      },
    );
  };

  const deleteDnsRecord = async (recordId) => {
    await performAction(
      `delete-dns-${recordId}`,
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/dns-records/${recordId}`, { method: 'DELETE' }),
      'DNS record deleted',
      async () => loadDomainDetails(selectedDomain),
    );
  };

  const saveContactProfile = async () => {
    if (!contactForm.first_name || !contactForm.last_name || !contactForm.email) {
      toast.error('First name, last name and email are required');
      return;
    }
    await performAction(
      'save-contact-profile',
      () => authorizedFetch('/api/domains/contact-profiles', {
        method: 'POST',
        body: JSON.stringify(contactForm),
      }),
      'Contact profile saved',
      async () => {
        const refreshed = await authorizedFetch('/api/domains/contact-profiles');
        setContactProfiles(refreshed || []);
        setContactForm(DEFAULT_CONTACT);
      },
    );
  };

  const savePortfolio = async () => {
    if (!portfolioName.trim()) {
      toast.error('Portfolio name is required');
      return;
    }
    await performAction(
      'save-portfolio',
      () => authorizedFetch('/api/domains/portfolios', {
        method: 'POST',
        body: JSON.stringify({ portfolio: portfolioName.trim() }),
      }),
      'Portfolio created',
      async () => {
        setPortfolios(await authorizedFetch('/api/domains/portfolios'));
        setPortfolioName('');
      },
    );
  };

  const assignPortfolio = async () => {
    const parsedDomains = portfolioDomains.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);
    if (!portfolioAssignName || parsedDomains.length === 0) {
      toast.error('Select a portfolio and provide at least one domain');
      return;
    }
    await performAction(
      'assign-portfolio',
      () => authorizedFetch('/api/domains/portfolios/assign', {
        method: 'POST',
        body: JSON.stringify({ portfolio: portfolioAssignName, domains: parsedDomains }),
      }),
      'Portfolio updated',
      async () => {
        await loadBaseData();
        setPortfolioDomains('');
      },
    );
  };

  const associateContacts = async () => {
    if (!selectedDomain) return;
    await performAction(
      'associate-contacts',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/contact-association`, {
        method: 'POST',
        body: JSON.stringify(domainContacts),
      }),
      'Domain contacts updated',
      async () => loadDomainDetails(selectedDomain),
    );
  };

  const addRegisteredNameserver = async () => {
    if (!selectedDomain || !registeredNsForm.new_host || !registeredNsForm.ip1) {
      toast.error('Host and primary IP are required');
      return;
    }
    await performAction(
      'add-registered-ns',
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/registered-nameservers`, {
        method: 'POST',
        body: JSON.stringify(registeredNsForm),
      }),
      'Registered nameserver added',
      async () => {
        await loadDomainDetails(selectedDomain);
        setRegisteredNsForm({ new_host: '', ip1: '', ip2: '' });
      },
    );
  };

  const removeRegisteredNameserver = async (host) => {
    await performAction(
      `remove-registered-ns-${host}`,
      () => authorizedFetch(`/api/domains/${encodeURIComponent(selectedDomain)}/registered-nameservers/${encodeURIComponent(host)}`, { method: 'DELETE' }),
      'Registered nameserver removed',
      async () => loadDomainDetails(selectedDomain),
    );
  };

  const providerLabel = overview?.provider === 'namesilo' ? 'Snagcart Domains' : 'Domain registrar';
  const priceEntries = Object.entries(prices || {}).slice(0, 6);
  const connectionLabel = overview?.connected ? 'Live' : (overview?.platform_registrar_configured ? 'Ready' : 'Setup pending');
  const connectionHint = overview?.connected
    ? 'Platform registrar is connected. Register domains using your domain credit wallet.'
    : (overview?.platform_registrar_configured
      ? 'Registrar configured — some endpoints may be temporarily unavailable.'
      : 'Domain registration will be enabled once the platform registrar is configured.');
  const registrationCostUsd = Number(searchResult?.price_registration || 0);
  const insufficientFunds = domainCreditsEnabled
    && registrationCostUsd > 0
    && Number(domainCreditBalanceUsd) < registrationCostUsd;

  return (
    <DashboardShell
      title="Domains"
      subtitle="Live registration, DNS, nameservers, privacy, lock state, contacts, portfolios and orders"
      activeNav="domains"
      fullscreen
      backPath="/dashboard"
      headerExtras={(
        <div className="flex items-center gap-2">
          <DomainCreditsPanel
            balanceUsd={domainCreditBalanceUsd}
            balanceCents={domainCreditBalanceCents}
            packs={domainCreditPacks}
            enabled={domainCreditsEnabled}
            stripeConfigured={domainCreditsStripeConfigured}
            open={domainCreditsModalOpen}
            onOpenChange={setDomainCreditsModalOpen}
            onRefresh={loadBaseData}
            onBalanceChange={(usd, cents) => {
              setDomainCreditBalanceUsd(usd ?? 0);
              setDomainCreditBalanceCents(cents ?? 0);
            }}
          />
          <Button
            onClick={loadBaseData}
            variant="outline"
            className="border-white/10 bg-white/[0.04] text-zinc-100 hover:bg-white/[0.08]"
            data-testid="domains-refresh-dashboard-button"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        </div>
      )}
    >
      <div className="h-full overflow-y-auto bg-[radial-gradient(circle_at_top_left,_rgba(17,94,89,0.34),_transparent_34%),linear-gradient(180deg,_#09090b_0%,_#0f1720_100%)] px-4 py-6 sm:px-6 lg:px-10" data-testid="domains-dashboard-page">
        <div className="mx-auto flex max-w-7xl flex-col gap-8">
          <ModuleDemoVideo moduleKey="domains-hosting" className="mb-0" />
          <section className="overflow-hidden rounded-[36px] border border-white/10 bg-[#071015]/90 p-6 sm:p-8" data-testid="domains-hero-section">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-xs uppercase tracking-[0.26em] text-emerald-200" data-testid="domains-provider-badge">
                  <Globe2 className="h-3.5 w-3.5" />
                  {providerLabel}
                </div>
                <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl" data-testid="domains-hero-title">
                  Manage every domain operation from one live control room.
                </h1>
                <p className="max-w-2xl text-sm text-zinc-300 sm:text-base" data-testid="domains-hero-description">
                  Search, register, renew, and attach domains using your domain credit wallet. Registration is billed from your balance; Snagcart handles the registrar purchase.
                </p>
              </div>
              <div className="grid min-w-full gap-4 sm:grid-cols-2 xl:min-w-[420px] xl:grid-cols-2">
                <StatCard label="Connection" value={connectionLabel} hint={connectionHint} accent={overview?.connected ? 'text-emerald-300' : 'text-amber-300'} testId="domains-stat-connection" />
                <StatCard label="Live domains" value={overview?.live_domains_count ?? '—'} hint="Detected directly from your connected registrar." accent="text-sky-300" testId="domains-stat-live-count" />
                <StatCard label="Portfolios" value={overview?.portfolio_count ?? '—'} hint="Segments for grouping domain inventory." accent="text-fuchsia-300" testId="domains-stat-portfolios" />
                <StatCard label="Domain credit" value={formatCurrency(domainCreditBalanceUsd)} hint="Buy credit, then register domains at live prices." accent="text-amber-300" testId="domains-stat-balance" />
              </div>
            </div>
            {overview?.connection_error && (
                  <div className="mt-6 flex items-start gap-3 rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-100" data-testid="domains-connection-warning">
                <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>Some registrar endpoints are temporarily unavailable. Core domain actions are still ready when the service responds.</span>
              </div>
            )}
          </section>

          <DomainCreditsBuySection
            balanceUsd={domainCreditBalanceUsd}
            packs={domainCreditPacks}
            enabled={domainCreditsEnabled}
            stripeConfigured={domainCreditsStripeConfigured}
            onOpenModal={() => setDomainCreditsModalOpen(true)}
            onBuyPack={() => setDomainCreditsModalOpen(true)}
          />

          <div className="flex flex-wrap gap-3" data-testid="domains-tabs-bar">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${activeTab === tab.id ? 'border-emerald-300 bg-emerald-300 text-black' : 'border-white/10 bg-white/[0.04] text-zinc-300 hover:bg-white/[0.08]'}`}
                data-testid={`domains-tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-[28px] border border-white/10 bg-white/[0.03]" data-testid="domains-loading-state">
              <Loader2 className="mr-3 h-5 w-5 animate-spin text-emerald-300" />
              <span className="text-zinc-300">Loading domain workspace…</span>
            </div>
          ) : (
            <>
              {activeTab === 'search' && (
                <section className="grid gap-6 xl:grid-cols-[1.3fr_0.9fr]" data-testid="domains-search-tab-panel">
                  <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                    <SectionHeading eyebrow="Acquisition" title="Search and register domains" subtitle="Run availability checks against your connected provider, compare prices and register instantly into your portfolio or a selected website." testId="domains-search-heading" />
                    <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_auto]">
                      <Input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="brandname.com" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-search-input" />
                      <Button onClick={searchDomain} className="h-12 bg-emerald-300 text-black hover:bg-emerald-200" data-testid="domains-search-submit-button">
                        {busyAction === 'search-domain' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                        Search live
                      </Button>
                    </div>

                    {searchResult && (
                      <div className="mt-8 rounded-[28px] border border-white/10 bg-[#09161f] p-5" data-testid="domains-search-result-card">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-[0.26em] text-zinc-500">Search result</p>
                            <p className="mt-2 text-2xl font-semibold text-white" data-testid="domains-search-result-name">{searchResult.domain_name}</p>
                            <p className={`mt-2 text-sm ${searchResult.available ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="domains-search-result-status">
                              {searchResult.available ? 'Available to register' : 'Not available or already attached in this workspace'}
                            </p>
                          </div>
                          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-right" data-testid="domains-search-result-price">
                            <p className="text-xs uppercase tracking-[0.24em] text-zinc-500">Yearly</p>
                            <p className="mt-2 text-2xl font-semibold text-emerald-200">{formatCurrency(searchResult.price_registration)}</p>
                            <p className="mt-1 text-xs text-zinc-500">Renewal {formatCurrency(searchResult.price_renewal)}</p>
                          </div>
                        </div>
                        <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_1fr]">
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                            <p className="text-xs uppercase tracking-[0.24em] text-zinc-500" data-testid="domains-register-mode-label">Registration mode</p>
                            <select
                              value={registerMode}
                              onChange={(event) => setRegisterMode(event.target.value)}
                              className="mt-3 h-11 w-full rounded-xl border border-white/10 bg-black/30 px-3 text-sm text-white"
                              data-testid="domains-register-mode-select"
                            >
                              <option value="portfolio">Portfolio only</option>
                              <option value="website">Register & attach to website</option>
                            </select>
                            {registerMode === 'website' && (
                              <select
                                value={registerWebsiteId}
                                onChange={(event) => setRegisterWebsiteId(event.target.value)}
                                className="mt-3 h-11 w-full rounded-xl border border-white/10 bg-black/30 px-3 text-sm text-white"
                                data-testid="domains-register-website-select"
                              >
                                <option value="">Select website</option>
                                {websites.map((site) => (
                                  <option key={site.id} value={site.id}>{site.name}</option>
                                ))}
                              </select>
                            )}
                            <p className="mt-2 text-xs text-zinc-500" data-testid="domains-register-mode-note">
                              {registerMode === 'website'
                                ? 'Registers the domain, deducts domain credit, and links it to your website with automatic DNS setup.'
                                : 'Domain goes into your portfolio — attach it to a website anytime from Portfolio or Live Operations.'}
                            </p>
                            <div className="mt-4 grid gap-3">
                              {insufficientFunds && (
                                <div className="rounded-2xl border border-amber-400/20 bg-amber-400/10 px-3 py-3 text-xs text-amber-100" data-testid="domains-registration-balance-warning">
                                  Insufficient domain credit for this registration ({formatCurrency(registrationCostUsd)} needed). Buy domain credit using the wallet button above.
                                </div>
                              )}
                              {registrationError && (
                                <div className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-3 py-3 text-xs text-rose-100" data-testid="domains-registration-error-alert">
                                  {registrationError}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="grid gap-3">
                            <Button onClick={() => registerDomain()} disabled={!searchResult.available} className="h-12 bg-white text-black hover:bg-zinc-200" data-testid="domains-register-portfolio-button">
                              {busyAction === 'register-standalone' || busyAction === 'register-attach' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                              {registerMode === 'website' ? 'Register & attach' : 'Register to portfolio'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                    <SectionHeading eyebrow="Pricing" title="TLD price matrix" testId="domains-pricing-heading" />
                    <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="domains-price-grid">
                      {priceEntries.map(([tld, value]) => (
                        <div key={tld} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid={`domains-price-card-${tld}`}>
                          <p className="text-lg font-semibold text-white">.{tld}</p>
                          <p className="mt-3 text-sm text-zinc-400">Register {formatCurrency(value.registration)}</p>
                          <p className="text-sm text-zinc-400">Renew {formatCurrency(value.renew)}</p>
                          <p className="text-sm text-zinc-400">Transfer {formatCurrency(value.transfer)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {activeTab === 'portfolio' && (
                <section className="grid gap-6 xl:grid-cols-[1fr_1.05fr]" data-testid="domains-portfolio-tab-panel">
                  <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                    <SectionHeading eyebrow="Inventory" title="All domains in your workspace" subtitle="Live registrar domains merge with locally attached website domains so you can manage everything from one table." testId="domains-portfolio-heading" />
                    <div className="mt-6 overflow-hidden rounded-[24px] border border-white/10">
                      <div className="hidden grid-cols-[1.4fr_1fr_0.8fr_0.9fr] gap-4 bg-white/[0.05] px-4 py-3 text-xs uppercase tracking-[0.22em] text-zinc-500 md:grid">
                        <span>Domain</span><span>Website</span><span>Connection</span><span>Status</span>
                      </div>
                      <div className="divide-y divide-white/10" data-testid="domains-portfolio-list">
                        {domains.length === 0 ? (
                          <div className="px-4 py-8 text-sm text-zinc-400" data-testid="domains-empty-state">No domains found yet.</div>
                        ) : domains.map((domain) => (
                          <button
                            key={domain.domain_name}
                            type="button"
                            onClick={() => { setSelectedDomain(domain.domain_name); setActiveTab('operations'); }}
                            className={`grid w-full gap-3 px-4 py-4 text-left transition-colors md:grid-cols-[1.4fr_1fr_0.8fr_0.9fr] ${selectedDomain === domain.domain_name ? 'bg-emerald-300/10' : 'hover:bg-white/[0.03]'}`}
                            data-testid={`domains-portfolio-row-${domain.domain_name.replace(/[^a-z0-9-]/g, '-')}`}
                          >
                            <div>
                              <p className="font-medium text-white">{domain.domain_name}</p>
                              <p className="mt-1 text-xs text-zinc-500">Provider {domain.registrar_provider === 'namesilo' ? 'connected registrar' : (domain.registrar_provider || 'registrar')}</p>
                            </div>
                            <div>
                              <p className="text-sm text-zinc-200">{domain.website_name || '(Not attached)'}</p>
                              <p className="mt-1 text-xs text-zinc-500">{domain.website_slug ? `/${domain.website_slug}` : domain.portfolio || 'Portfolio'}</p>
                            </div>
                            <div className="text-sm text-zinc-200">
                              <p>{domainStatusLabel(domain.domain_status)}</p>
                              <p className="mt-1 text-xs text-zinc-500">{domain.is_published ? 'Site published' : 'Site draft'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-emerald-200">{domain.status || 'Active'}</p>
                              <p className="mt-1 text-xs text-zinc-500">{domain.privacy ? 'Privacy on' : 'Privacy off'} · {domain.expires_at || 'No expiry'}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                    <SectionHeading eyebrow="Orders" title="Recent registrar orders" subtitle="Registrations and renewals are surfaced here from the connected account so you can verify financial activity quickly." testId="domains-orders-heading" />
                    <div className="mt-6 space-y-3" data-testid="domains-orders-list">
                      {orders.length === 0 ? (
                        <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-5 text-sm text-zinc-400" data-testid="domains-orders-empty-state">No recent orders available.</div>
                      ) : orders.map((order) => (
                        <div key={order.order_number} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={`domains-order-card-${order.order_number}`}>
                          <div>
                            <p className="text-sm font-medium text-white">Order #{order.order_number}</p>
                            <p className="mt-1 text-xs text-zinc-500">{order.order_date || 'Unknown date'} · {order.method || 'Account funds'}</p>
                          </div>
                          <p className="text-sm font-semibold text-amber-200">{formatCurrency(order.total)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {activeTab === 'operations' && (
                <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]" data-testid="domains-operations-tab-panel">
                  <div className="space-y-6">
                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="Live domain" title={selectedDomain || 'Choose a domain'} subtitle="Select a live domain from the portfolio tab, then manage its lock state, privacy, nameservers, transfer status and auth code here." testId="domains-operations-heading" />
                      <div className="mt-6">
                        <select value={selectedDomain} onChange={(event) => setSelectedDomain(event.target.value)} className="h-12 w-full rounded-2xl border border-white/10 bg-black/30 px-4 text-sm text-white" data-testid="domains-selected-domain-select">
                          <option value="">Select a domain</option>
                          {domains.map((domain) => (
                            <option key={domain.domain_name} value={domain.domain_name}>{domain.domain_name}</option>
                          ))}
                        </select>
                      </div>
                      {selectedDomainData && (
                        <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="domains-selected-domain-summary">
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                            <p className="text-xs uppercase tracking-[0.22em] text-zinc-500">Protection</p>
                            <p className="mt-3 text-sm text-zinc-200">{selectedDomainData.locked ? 'Locked' : 'Unlocked'} · {selectedDomainData.privacy ? 'WHOIS privacy on' : 'WHOIS privacy off'}</p>
                            <p className="mt-1 text-xs text-zinc-500">{selectedDomainData.auto_renew ? 'Auto-renew active' : 'Manual renewal mode'}</p>
                          </div>
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                            <p className="text-xs uppercase tracking-[0.22em] text-zinc-500">Routing</p>
                            <p className="mt-3 text-sm text-zinc-200">{(domainDetails?.nameservers || []).join(', ') || 'No nameservers loaded'}</p>
                            <p className="mt-1 text-xs text-zinc-500">Expires {selectedDomainData.expires_at || 'Unknown'}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="Website" title="Attach to a published site" subtitle="Link this domain to one of your websites, configure DNS, and go live on your custom URL." testId="domains-website-connection-heading" />
                      <div className="mt-6 space-y-4">
                        <select
                          value={attachWebsiteId}
                          onChange={(event) => setAttachWebsiteId(event.target.value)}
                          disabled={!selectedDomain}
                          className="h-12 w-full rounded-2xl border border-white/10 bg-black/30 px-4 text-sm text-white"
                          data-testid="domains-attach-website-select"
                        >
                          <option value="">Select website to attach</option>
                          {websites.map((site) => (
                            <option key={site.id} value={site.id}>{site.name}{site.is_published ? ' (published)' : ''}</option>
                          ))}
                        </select>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Button onClick={attachDomain} disabled={!selectedDomain || !attachWebsiteId} className="h-12 bg-emerald-300 text-black hover:bg-emerald-200" data-testid="domains-attach-button">
                            {busyAction === 'attach-domain' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link2 className="mr-2 h-4 w-4" />}
                            Attach domain
                          </Button>
                          <Button onClick={detachDomain} disabled={!selectedDomain || !domainConnection?.website_id} variant="outline" className="h-12 border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.08]" data-testid="domains-detach-button">
                            {busyAction === 'detach-domain' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlink className="mr-2 h-4 w-4" />}
                            Detach
                          </Button>
                        </div>
                        {domainConnection && (
                          <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="domains-connection-checklist">
                            <p className="text-xs uppercase tracking-[0.22em] text-zinc-500">Go-live checklist</p>
                            <div className="mt-4 space-y-2">
                              {(domainConnection.checklist || []).map((item) => (
                                <div key={item.key} className="flex items-center gap-2 text-sm">
                                  <CheckCircle2 className={`h-4 w-4 ${item.done ? 'text-emerald-300' : 'text-zinc-600'}`} />
                                  <span className={item.done ? 'text-zinc-200' : 'text-zinc-500'}>{item.label}</span>
                                </div>
                              ))}
                            </div>
                            <p className="mt-4 text-sm text-zinc-300">
                              Status: <span className="text-emerald-200">{domainStatusLabel(domainConnection.domain_status)}</span>
                            </p>
                            {domainConnection.public_urls?.slug_url && (
                              <a href={domainConnection.public_urls.slug_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-xs text-sky-300 hover:text-sky-200">
                                <ExternalLink className="h-3.5 w-3.5" /> Open Snagcart URL
                              </a>
                            )}
                            {domainConnection.public_urls?.custom_domain_url && (
                              <a href={domainConnection.public_urls.custom_domain_url} target="_blank" rel="noreferrer" className="mt-2 ml-4 inline-flex items-center gap-2 text-xs text-sky-300 hover:text-sky-200">
                                <ExternalLink className="h-3.5 w-3.5" /> Open custom domain
                              </a>
                            )}
                            {domainConnection.instructions && (
                              <div className="mt-4 rounded-xl border border-white/5 bg-black/30 p-3 text-xs text-zinc-400">
                                <p>Manual DNS: A @ → {domainConnection.instructions.a_record?.value}</p>
                                <p className="mt-1">CNAME www → {domainConnection.instructions.cname_record?.value}</p>
                              </div>
                            )}
                            <Button onClick={configureHostingDns} disabled={!selectedDomain} className="mt-4 h-11 bg-sky-300 text-black hover:bg-sky-200" data-testid="domains-configure-dns-button">
                              {busyAction === 'configure-dns' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Network className="mr-2 h-4 w-4" />}
                              Auto-configure DNS
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="Security" title="Lock, privacy, renewals and transfers" subtitle="These actions hit the live registrar API immediately when your key is configured." testId="domains-security-heading" />
                      <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        <Button onClick={() => toggleLock(true)} disabled={!selectedDomain} className="h-12 bg-white text-black hover:bg-zinc-200" data-testid="domains-lock-button">{busyAction === 'lock-true' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}Lock domain</Button>
                        <Button onClick={() => toggleLock(false)} disabled={!selectedDomain} variant="outline" className="h-12 border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.08]" data-testid="domains-unlock-button">{busyAction === 'lock-false' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlock className="mr-2 h-4 w-4" />}Unlock domain</Button>
                        <Button onClick={() => togglePrivacy(true)} disabled={!selectedDomain} className="h-12 bg-emerald-300 text-black hover:bg-emerald-200" data-testid="domains-enable-privacy-button">{busyAction === 'privacy-true' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}Enable privacy</Button>
                        <Button onClick={() => togglePrivacy(false)} disabled={!selectedDomain} variant="outline" className="h-12 border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.08]" data-testid="domains-disable-privacy-button">{busyAction === 'privacy-false' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldOff className="mr-2 h-4 w-4" />}Disable privacy</Button>
                        <Button onClick={() => toggleAutoRenew(true)} disabled={!selectedDomain} className="h-12 bg-sky-300 text-black hover:bg-sky-200" data-testid="domains-enable-auto-renew-button">{busyAction === 'auto-renew-true' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Enable auto-renew</Button>
                        <Button onClick={() => toggleAutoRenew(false)} disabled={!selectedDomain} variant="outline" className="h-12 border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.08]" data-testid="domains-disable-auto-renew-button">{busyAction === 'auto-renew-false' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Disable auto-renew</Button>
                        <Button onClick={renewDomain} disabled={!selectedDomain} className="h-12 bg-amber-300 text-black hover:bg-amber-200" data-testid="domains-renew-button">{busyAction === 'renew-domain' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BadgeDollarSign className="mr-2 h-4 w-4" />}Renew 1 year</Button>
                        <Button onClick={requestAuthCode} disabled={!selectedDomain} variant="outline" className="h-12 border-white/10 bg-white/[0.03] text-white hover:bg-white/[0.08]" data-testid="domains-auth-code-button">{busyAction === 'auth-code' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileKey2 className="mr-2 h-4 w-4" />}Request EPP code</Button>
                        <Button onClick={requestTransferStatus} disabled={!selectedDomain} className="h-12 bg-fuchsia-300 text-black hover:bg-fuchsia-200 sm:col-span-2" data-testid="domains-transfer-status-button">{busyAction === 'transfer-status' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}Check transfer status</Button>
                      </div>
                      {transferStatus && (
                        <div className="mt-4 rounded-2xl border border-white/10 bg-black/20 px-4 py-4 text-sm text-zinc-200" data-testid="domains-transfer-status-result">
                          <p className="font-medium text-white">{transferStatus.status || 'No status returned'}</p>
                          <p className="mt-1 text-zinc-400">{transferStatus.message || transferStatus.detail || 'No transfer details available.'}</p>
                          <p className="mt-2 text-xs text-zinc-500">{transferStatus.date || 'No timestamp provided'}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="DNS" title="Manage resource records" subtitle="Add new records instantly. Existing records can be removed from the live zone manager below." testId="domains-dns-heading" />
                      <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        <select value={dnsForm.type} onChange={(event) => setDnsForm((current) => ({ ...current, type: event.target.value }))} className="h-12 rounded-2xl border border-white/10 bg-black/30 px-4 text-sm text-white" data-testid="domains-dns-type-select">
                          {['A', 'AAAA', 'CNAME', 'MX', 'TXT'].map((type) => <option key={type} value={type}>{type}</option>)}
                        </select>
                        <Input value={dnsForm.host} onChange={(event) => setDnsForm((current) => ({ ...current, host: event.target.value }))} placeholder="@ or www" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-dns-host-input" />
                        <Input value={dnsForm.value} onChange={(event) => setDnsForm((current) => ({ ...current, value: event.target.value }))} placeholder="Record value" className="h-12 border-white/10 bg-black/30 text-white sm:col-span-2" data-testid="domains-dns-value-input" />
                        <Input type="number" value={dnsForm.ttl} onChange={(event) => setDnsForm((current) => ({ ...current, ttl: event.target.value }))} placeholder="3600" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-dns-ttl-input" />
                        <Input type="number" value={dnsForm.distance} onChange={(event) => setDnsForm((current) => ({ ...current, distance: event.target.value }))} placeholder="MX distance (optional)" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-dns-distance-input" />
                      </div>
                      <Button onClick={saveDnsRecord} disabled={!selectedDomain} className="mt-4 h-12 bg-emerald-300 text-black hover:bg-emerald-200" data-testid="domains-dns-save-button">{busyAction === 'save-dns' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Add DNS record</Button>
                      <div className="mt-6 space-y-3" data-testid="domains-dns-records-list">
                        {dnsRecords.length === 0 ? (
                          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-5 text-sm text-zinc-400" data-testid="domains-dns-empty-state">No DNS records loaded.</div>
                        ) : dnsRecords.map((record) => (
                          <div key={record.record_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={`domains-dns-record-${record.record_id}`}>
                            <div>
                              <p className="text-sm font-medium text-white">{record.type} · {record.host}</p>
                              <p className="mt-1 text-xs text-zinc-400">{record.value} · TTL {record.ttl}{record.distance ? ` · Priority ${record.distance}` : ''}</p>
                            </div>
                            <Button onClick={() => deleteDnsRecord(record.record_id)} variant="outline" className="border-rose-400/20 bg-rose-400/10 text-rose-100 hover:bg-rose-400/20" data-testid={`domains-dns-delete-${record.record_id}`}>
                              {busyAction === `delete-dns-${record.record_id}` ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="Routing" title="Nameservers and host records" subtitle="Change active nameservers, then manage custom registered nameserver hosts directly for the selected domain." testId="domains-routing-heading" />
                      <textarea value={nameserverText} onChange={(event) => setNameserverText(event.target.value)} placeholder="ns1.provider.com&#10;ns2.provider.com" className="mt-6 min-h-[120px] w-full rounded-[24px] border border-white/10 bg-black/30 px-4 py-4 text-sm text-white outline-none" data-testid="domains-nameserver-textarea" />
                      <Button onClick={saveNameservers} disabled={!selectedDomain} className="mt-4 h-12 bg-white text-black hover:bg-zinc-200" data-testid="domains-save-nameservers-button">{busyAction === 'save-nameservers' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Network className="mr-2 h-4 w-4" />}Save nameservers</Button>

                      <div className="mt-6 grid gap-3 sm:grid-cols-3">
                        <Input value={registeredNsForm.new_host} onChange={(event) => setRegisteredNsForm((current) => ({ ...current, new_host: event.target.value }))} placeholder="ns1" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-registered-ns-host-input" />
                        <Input value={registeredNsForm.ip1} onChange={(event) => setRegisteredNsForm((current) => ({ ...current, ip1: event.target.value }))} placeholder="Primary IP" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-registered-ns-ip1-input" />
                        <Input value={registeredNsForm.ip2} onChange={(event) => setRegisteredNsForm((current) => ({ ...current, ip2: event.target.value }))} placeholder="Secondary IP" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-registered-ns-ip2-input" />
                      </div>
                      <Button onClick={addRegisteredNameserver} disabled={!selectedDomain} className="mt-4 h-12 bg-sky-300 text-black hover:bg-sky-200" data-testid="domains-registered-ns-save-button">{busyAction === 'add-registered-ns' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Add registered nameserver</Button>

                      <div className="mt-6 space-y-3" data-testid="domains-registered-ns-list">
                        {registeredNameservers.length === 0 ? (
                          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-5 text-sm text-zinc-400" data-testid="domains-registered-ns-empty-state">No registered nameservers found.</div>
                        ) : registeredNameservers.map((entry) => (
                          <div key={entry.host} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={`domains-registered-ns-${entry.host}`}>
                            <div>
                              <p className="text-sm font-medium text-white">{entry.host}</p>
                              <p className="mt-1 text-xs text-zinc-400">{entry.ips.join(', ')}</p>
                            </div>
                            <Button onClick={() => removeRegisteredNameserver(entry.host)} variant="outline" className="border-rose-400/20 bg-rose-400/10 text-rose-100 hover:bg-rose-400/20" data-testid={`domains-registered-ns-delete-${entry.host}`}>
                              {busyAction === `remove-registered-ns-${entry.host}` ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}Remove
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {activeTab === 'settings' && (
                <section className="grid gap-6 xl:grid-cols-[1fr_1fr]" data-testid="domains-settings-tab-panel">
                  <div className="space-y-6">
                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="Contacts" title="Create contact profiles" subtitle="Build reusable registrant, administrative, technical and billing profiles for fast live registrations and domain contact updates." testId="domains-contact-heading" />
                      <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        <Input value={contactForm.first_name} onChange={(event) => setContactForm((current) => ({ ...current, first_name: event.target.value }))} placeholder="First name" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-first-name-input" />
                        <Input value={contactForm.last_name} onChange={(event) => setContactForm((current) => ({ ...current, last_name: event.target.value }))} placeholder="Last name" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-last-name-input" />
                        <Input value={contactForm.email} onChange={(event) => setContactForm((current) => ({ ...current, email: event.target.value }))} placeholder="Email" className="h-12 border-white/10 bg-black/30 text-white sm:col-span-2" data-testid="domains-contact-email-input" />
                        <Input value={contactForm.phone} onChange={(event) => setContactForm((current) => ({ ...current, phone: event.target.value }))} placeholder="Phone" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-phone-input" />
                        <Input value={contactForm.company} onChange={(event) => setContactForm((current) => ({ ...current, company: event.target.value }))} placeholder="Company" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-company-input" />
                        <Input value={contactForm.address} onChange={(event) => setContactForm((current) => ({ ...current, address: event.target.value }))} placeholder="Address" className="h-12 border-white/10 bg-black/30 text-white sm:col-span-2" data-testid="domains-contact-address-input" />
                        <Input value={contactForm.city} onChange={(event) => setContactForm((current) => ({ ...current, city: event.target.value }))} placeholder="City" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-city-input" />
                        <Input value={contactForm.state} onChange={(event) => setContactForm((current) => ({ ...current, state: event.target.value }))} placeholder="State" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-state-input" />
                        <Input value={contactForm.zip} onChange={(event) => setContactForm((current) => ({ ...current, zip: event.target.value }))} placeholder="ZIP" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-zip-input" />
                        <Input value={contactForm.country} onChange={(event) => setContactForm((current) => ({ ...current, country: event.target.value }))} placeholder="Country code" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-contact-country-input" />
                      </div>
                      <Button onClick={saveContactProfile} className="mt-4 h-12 bg-emerald-300 text-black hover:bg-emerald-200" data-testid="domains-contact-save-button">{busyAction === 'save-contact-profile' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}Save contact profile</Button>

                      <div className="mt-6 space-y-3" data-testid="domains-contact-list">
                        {contactProfiles.length === 0 ? (
                          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-5 text-sm text-zinc-400" data-testid="domains-contact-empty-state">No contact profiles loaded.</div>
                        ) : contactProfiles.map((contact) => (
                          <div key={contact.contact_id} className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={`domains-contact-card-${contact.contact_id}`}>
                            <p className="text-sm font-medium text-white">{contact.first_name} {contact.last_name}</p>
                            <p className="mt-1 text-xs text-zinc-400">{contact.email} · {contact.phone}</p>
                            <p className="mt-1 text-xs text-zinc-500">ID {contact.contact_id} · {contact.nickname || 'No nickname'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="Portfolio organization" title="Create portfolios and assign domains" subtitle="Use portfolios to group customer domains, side projects or campaigns directly in your connected registrar." testId="domains-portfolio-settings-heading" />
                      <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_auto]">
                        <Input value={portfolioName} onChange={(event) => setPortfolioName(event.target.value)} placeholder="Example: Client domains" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-portfolio-name-input" />
                        <Button onClick={savePortfolio} className="h-12 bg-white text-black hover:bg-zinc-200" data-testid="domains-portfolio-save-button">{busyAction === 'save-portfolio' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FolderKanban className="mr-2 h-4 w-4" />}Create</Button>
                      </div>
                      <div className="mt-6 rounded-[24px] border border-white/10 bg-black/20 p-4">
                        <label className="text-xs uppercase tracking-[0.22em] text-zinc-500">Assign domains to a portfolio</label>
                        <select value={portfolioAssignName} onChange={(event) => setPortfolioAssignName(event.target.value)} className="mt-3 h-12 w-full rounded-2xl border border-white/10 bg-black/30 px-4 text-sm text-white" data-testid="domains-portfolio-assign-select">
                          <option value="">Select portfolio</option>
                          {portfolios.map((portfolio) => <option key={portfolio} value={portfolio}>{portfolio}</option>)}
                        </select>
                        <Input value={portfolioDomains} onChange={(event) => setPortfolioDomains(event.target.value)} placeholder="domain-one.com, domain-two.com" className="mt-3 h-12 border-white/10 bg-black/30 text-white" data-testid="domains-portfolio-domains-input" />
                        <Button onClick={assignPortfolio} className="mt-3 h-12 w-full bg-emerald-300 text-black hover:bg-emerald-200" data-testid="domains-portfolio-assign-button">{busyAction === 'assign-portfolio' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Boxes className="mr-2 h-4 w-4" />}Assign domains</Button>
                      </div>
                      <div className="mt-6 space-y-3" data-testid="domains-portfolio-tags-list">
                        {portfolios.length === 0 ? (
                          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-5 text-sm text-zinc-400" data-testid="domains-portfolios-empty-state">No portfolios created yet.</div>
                        ) : portfolios.map((portfolio) => (
                          <div key={portfolio} className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={`domains-portfolio-tag-${portfolio.replace(/[^a-z0-9-]/gi, '-')}`}>
                            <span className="text-sm text-zinc-200">{portfolio}</span>
                            <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Portfolio</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-[32px] border border-white/10 bg-white/[0.03] p-6 sm:p-8">
                      <SectionHeading eyebrow="Domain contacts" title="Associate profiles with the selected domain" subtitle="Push registrant, admin, tech and billing contacts to the live domain using the contact IDs returned by your connected registrar." testId="domains-associate-heading" />
                      <div className="mt-6 grid gap-3 sm:grid-cols-2">
                        <Input value={domainContacts.registrant} onChange={(event) => setDomainContacts((current) => ({ ...current, registrant: event.target.value }))} placeholder="Registrant ID" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-domain-contact-registrant-input" />
                        <Input value={domainContacts.administrative} onChange={(event) => setDomainContacts((current) => ({ ...current, administrative: event.target.value }))} placeholder="Administrative ID" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-domain-contact-admin-input" />
                        <Input value={domainContacts.technical} onChange={(event) => setDomainContacts((current) => ({ ...current, technical: event.target.value }))} placeholder="Technical ID" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-domain-contact-technical-input" />
                        <Input value={domainContacts.billing} onChange={(event) => setDomainContacts((current) => ({ ...current, billing: event.target.value }))} placeholder="Billing ID" className="h-12 border-white/10 bg-black/30 text-white" data-testid="domains-domain-contact-billing-input" />
                      </div>
                      <Button onClick={associateContacts} disabled={!selectedDomain} className="mt-4 h-12 bg-sky-300 text-black hover:bg-sky-200" data-testid="domains-domain-contact-save-button">{busyAction === 'associate-contacts' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}Apply to selected domain</Button>
                      <p className="mt-4 text-xs text-zinc-500" data-testid="domains-domain-contact-note">Choose a domain in the Live Operations tab first. Use the contact cards above to copy each ID.</p>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}

        </div>
      </div>
    </DashboardShell>
  );
}